import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, addDoc, collection, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';
import Burger from './Burger';
import "../App.css";

const PROGRESS_KEYS = [
  "currentLevel",
  "level2Stats",
  "level3Stats",
  "level4Stats",
];

const EndGame = ({ level2Stats, level3Stats, level4Stats }) => {
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminChecked, setAdminChecked] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSucceeded, setSaveSucceeded] = useState(false);
  const autoSaveAttemptedRef = useRef(false);
  // State to toggle between chart and table view:
  const [displayMode] = useState('table'); // 'chart' or 'table'
  
  const navigate = useNavigate();
  const auth = getAuth();
  const firestore = getFirestore();

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setIsAdmin(false);
      setAdminChecked(true);
      return;
    }

    (async () => {
      try {
        const regRef = doc(firestore, 'registrations', currentUser.uid);
        const regSnap = await getDoc(regRef);
        setIsAdmin(!!regSnap.data()?.isAdmin);
      } catch (e) {
        console.error('admin check error', e);
        setIsAdmin(false);
      } finally {
        setAdminChecked(true);
      }
    })();
  }, [auth, firestore]);

  const sumStats = (key) =>
    (level2Stats?.[key] || 0) + (level3Stats?.[key] || 0) + (level4Stats?.[key] || 0);
  const points2 = parseFloat(level2Stats?.finalScore) || 0;
  const points3 = parseFloat(level3Stats?.finalScore) || 0;
  const points4 = parseFloat(level4Stats?.finalScore) || 0;
  const hasAllLevelStats = !!(level2Stats && level3Stats && level4Stats);

  const totalPoints = points2 + points3 + points4; // Now a sum of numbers
  const averageScore = totalPoints / 3; // Now a valid average

  const totalEnergyConsumed = sumStats('totalEnergyConsumed');
  const totalPenaltyCost = sumStats('totalPenaltyCost');
  const totalCost = sumStats('totalCost');
  const totalEmissions = sumStats('totalEmissions');

  const clearGameProgress = useCallback(() => {
    PROGRESS_KEYS.forEach(k => localStorage.removeItem(k));
  }, []);

  // Saving results to Firestore
  const handleSaveResults = useCallback(async ({ manual = false } = {}) => {
    setSaving(true);
    setSaveError(null);
    try {
      const isGuest = localStorage.getItem('guestMode') === 'true';
      if (isGuest) {
        alert('Guest mode: results are not saved.');
        setSaving(false);
        navigate('/');
        return;
      }

      const currentUser = auth.currentUser;
      if (!currentUser) {
        alert('User not logged in!');
        setSaving(false);
        return;
      }

      // choose target UID
      let targetUid = currentUser.uid;

      if (isAdmin) {
        const nextUser = (localStorage.getItem('nextUserName') || '').trim();
        if (nextUser) {
          const q = query(collection(firestore, 'registrations'), where('username', '==', nextUser));
          const qs = await getDocs(q);
          if (!qs.empty) {
            const hit = qs.docs[0];
            targetUid = hit.id;
          }
        }
      }

      // prepare data
      const finalData = {
        level2Stats, level3Stats, level4Stats,
        totalEnergyConsumed, totalPenaltyCost, totalCost, totalEmissions,
        totalScore: averageScore,
        uid: targetUid,
        createdAt: serverTimestamp(),
      };

      // write a new result document
      await addDoc(collection(firestore, 'results'), finalData);
      
      try {
        const user = auth.currentUser;
        const tok = await user.getIdTokenResult(true);
        const isAdminClaim = !!tok.claims.admin;
        if (isAdminClaim) {
          // 1) fetch registration of the *player* (targetUid)
          const regSnap = await getDoc(doc(firestore, 'registrations', targetUid));
          const reg = regSnap.exists() ? regSnap.data() : {};
          // console.log('Fetched registration for leaderboard upsert:', reg);

          // 2) fetch current leaderboard entry (public read)
          const lbRef = doc(firestore, 'leaderboard', targetUid);
          const lbSnap = await getDoc(lbRef);
          const currentBest = lbSnap.exists() ? (lbSnap.data().bestScore ?? -Infinity) : -Infinity;
          // console.log('Current best score on leaderboard:', currentBest);

          // 3) only write if better
          if ((finalData.totalScore ?? 0) > currentBest) {
            const username = (localStorage.getItem('nextUserName') || '').trim();
            await setDoc(lbRef, {
              uid: targetUid,
              bestScore: Number(finalData.totalScore) || 0,
              updatedAt: serverTimestamp(),
              name: reg.name ?? null,
              username: username ?? null,
              company: reg.company ?? null,
            }, { merge: true });
          }
        } 
      } catch (e) {
        // Don’t block the flow if leaderboard upsert fails
        console.error('Leaderboard upsert failed:', e);
      }

      setSaveSucceeded(true);
      clearGameProgress();

    } catch (error) {
        setSaveError(manual ? 'Manual save failed. Please try again.' : 'Automatic save failed. You can save manually.');
    } finally {
      localStorage.removeItem('guestMode');
      setSaving(false);
      // navigate('/');
    }
  }, 
  [
    level2Stats,
    level3Stats,
    level4Stats,
    totalEnergyConsumed,
    totalPenaltyCost,
    totalCost,
    totalEmissions,
    averageScore,
    isAdmin,
    auth,
    firestore,
    navigate,
    clearGameProgress,
  ]);

  useEffect(() => {
    if (autoSaveAttemptedRef.current) return;
    if (!adminChecked || !hasAllLevelStats) return;

    autoSaveAttemptedRef.current = true;
    handleSaveResults();
  }, [adminChecked, hasAllLevelStats, handleSaveResults]);

  // Data for the charts (using 'cost', 'energy', and 'emissions')
  const chartData = [
    {
      level: '',
      cost: level2Stats?.totalCost * 20  || 0,
      energy: level2Stats?.totalEnergyConsumed * 20 / 1000 || 0,
      emissions: level2Stats?.totalEmissions / 1000000 * 20|| 0
    },
    {
      level: '',
      cost: level3Stats?.totalCost * 20  || 0,
      energy: level3Stats?.totalEnergyConsumed * 20 / 1000 || 0,
      emissions: level3Stats?.totalEmissions /1000000 * 20 || 0
    },
    {
      level: '',
      cost: level4Stats?.totalCost * 20 || 0,
      energy: level4Stats?.totalEnergyConsumed * 20 / 1000 || 0,
      emissions: level4Stats?.totalEmissions /1000000 * 20 || 0
    }
  ];

  const playerCost = (totalCost * 15.66).toLocaleString("it-IT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  const playerEmissions = ((totalEmissions / 1000000) * 8.05).toLocaleString("it-IT", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const playerEnergy = ((totalEnergyConsumed / 1000) * 15.67).toLocaleString("it-IT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  
  return (
    <div className="endgame-container">
      <Burger />
      <div className="endgame-background">
        <header>
          <img
            src="/ser.png"
            alt="School energy report"
            style={{
              height: "140px",
              transform: "translateY(0px)",
              position: "absolute",
              left: "0",
              right: "0",
              margin: "auto",
            }}
          />
          <p className="endgame-subtitle">Summary / Year 2026</p>
        </header>

        <div className="endgame-top-section">
          <div className="endgame-top-row">
            <section className="endgame-levels">
              <div className="level-mini">
                <p className="level-label ">Level 1</p>
                <p className="level-value ">{points2?.toFixed(1)}</p>
              </div>
              <div className="level-mini">
                <p className="level-label ">Level 2</p>
                <p className="level-value ">{points3?.toFixed(1)}</p>
              </div>
              <div className="level-mini">
                <p className="level-label ">Level 3</p>
                <p className="level-value ">{points4?.toFixed(1)}</p>
              </div>
            </section>
            <section className="endgame-score-card">
              <p className="endgame-score-label">Final score</p>
              <p className="endgame-score-value">{averageScore.toFixed(1)}</p>
            </section>
          </div>

          <p className="endgame-penalty-badge">
            Motivation fee for high return temperature:{" "}
            {totalPenaltyCost.toFixed(0)} DKK
          </p>
        </div>

        {/* Toggle Button */}
        {/* <div style={{ textAlign: 'center', margin: '1rem' }}>
        <button className='admin-button' onClick={() =>
          setDisplayMode(displayMode === 'chart' ? 'table' : 'chart')
        }>
          {displayMode === 'chart' ? 'Show Tab' : 'Show Graphs'}
        </button>
      </div>
       */}
        {/* Content: Charts or Table */}
        {displayMode === "chart" ? (
          <div className="charts-container">
            {/* Cost Chart */}
            <div className="chart-card">
              <h4>Cost DKK</h4>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="level" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="cost" fill="#b12f74" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Energy Chart */}
            <div className="chart-card">
              <h4>Energy (MWh)</h4>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="level" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="energy" fill="#F4F378" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Emissions Chart */}
            <div className="chart-card">
              <h4>Emissions (t CO₂)</h4>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="level" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="emissions" fill="#f18f01" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <main className="endgame-main">
            <div className="endgame-table-wrap">
              <table className="endgame-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Baseline</th>
                    <th>Player</th>
                    <th>MPC</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="row-costs">
                    <td className="metric-name">
                      <span className="metric-content">
                        <span className="metric-text">Costs</span>
                        <img
                          src="/cost.svg"
                          alt=""
                          className="metric-icon"
                          aria-hidden="true"
                        />
                      </span>
                    </td>
                    <td>392.911 DKK</td>
                    <td>{playerCost} DKK</td>
                    <td>312.164 DKK</td>
                  </tr>
                  <tr className="row-emissions">
                    <td className="metric-name">
                      <span className="metric-content">
                        <span className="metric-text">Emissions</span>
                        <img
                          src="/CO2.svg"
                          alt=""
                          className="metric-icon"
                          aria-hidden="true"
                        />
                      </span>
                    </td>
                    <td>47,6 t CO₂</td>
                    <td>{playerEmissions} t CO₂</td>
                    <td>34,9 t CO₂</td>
                  </tr>
                  <tr className="row-energy">
                    <td className="metric-name">
                      <span className="metric-content">
                        <span className="metric-text">Energy</span>
                        <img
                          src="/heating_energy.svg"
                          alt=""
                          className="metric-icon"
                          aria-hidden="true"
                        />
                      </span>
                    </td>
                    <td>673 MWh</td>
                    <td>{playerEnergy} MWh</td>
                    <td>557 MWh</td>
                  </tr>
                </tbody>
              </table>

              {saveError && !saving && (
                <p
                  style={{
                    margin: 0,
                    marginTop: "-12px",
                    color: "red",
                    fontSize: "16px",
                    fontWeight: "500",
                    background: "white",
                    padding: "8px",
                    borderRadius: "12px",
                  }}
                >
                  {saveError}
                </p>
              )}
              <div className="endgame-actions">
                {!saveSucceeded && saving && (
                  <p
                    style={{
                      margin: 0,
                      color: "#eef6ff",
                      fontSize: "16px",
                      fontWeight: "500",
                    }}
                  >
                    Saving results...
                  </p>
                )}
                {saveSucceeded && !saving && (
                  <p
                    style={{
                      margin: 0,
                      color: "#eef6ff",
                      fontSize: "16px",
                      fontWeight: "500",
                    }}
                  >
                    Results saved successfully!
                  </p>
                )}
                {saveError && (
                  <button
                    className="admin-button endgame-btn-primary"
                    onClick={() => handleSaveResults({ manual: true })}
                    disabled={saving}
                  >
                    Save the results
                  </button>
                )}
                <button
                  disabled={saving}
                  className="admin-button endgame-btn-secondary"
                  onClick={() => navigate("/")}
                >
                  {"Go back"}
                </button>
              </div>
            </div>
          </main>
        )}
      </div>
    </div>
  );
};

export default EndGame;
